import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import { hasMinRole } from "@/lib/constants";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import type { DBEquipmentCategory, DBEquipmentItem } from "@/lib/db-types";

const PUBLIC_COLUMNS =
  "id, name, category_id, department_id, quantity, serial_numbers, status, bookable, insured_until, insurance_declined, purchase_date, retired_date, rental_price_day, parts, comment, source, legacy_id, created_by, created_at, updated_at, product_id, product_skip_reason";
const MANAGER_COLUMNS = `${PUBLIC_COLUMNS}, insurance_value, purchase_price, extra_price`;

/**
 * Avdeling og utstyrsrettighet for aktiv bruker. Admin og utstyrsansvarlige
 * leser basistabellen med beløp; andre leser equipment_items_public.
 */
export function useEquipmentAccess() {
  const { activeUser } = useImpersonation();
  const query = useQuery({
    queryKey: ["equipment-profile", activeUser?.id],
    enabled: !!activeUser?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("users")
        .select("department_id, can_manage_equipment")
        .eq("id", activeUser!.id)
        .single();
      if (error) throw error;
      return data as { department_id: string | null; can_manage_equipment: boolean };
    },
    staleTime: 5 * 60 * 1000,
  });
  const isAdmin = hasMinRole(activeUser?.role ?? "user", "admin");
  return {
    isAdmin,
    canManage: isAdmin || !!query.data?.can_manage_equipment,
    departmentId: query.data?.department_id ?? null,
    isLoading: !activeUser || query.isLoading,
  };
}

export function useEquipmentCategories(enabled = true) {
  return useQuery({
    queryKey: ["equipment-categories"],
    enabled,
    queryFn: async (): Promise<DBEquipmentCategory[]> => {
      const { data, error } = await supabase
        .from("equipment_categories")
        .select("id, name, sort_order, bookable")
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as DBEquipmentCategory[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Hele registeret (ikke slettet), sortert på navn. */
export function useEquipmentList(canManage: boolean, enabled = true) {
  return useQuery({
    queryKey: ["equipment", { canManage }],
    enabled,
    queryFn: async (): Promise<DBEquipmentItem[]> => {
      const rows = canManage
        ? await fetchAllPaginated<DBEquipmentItem>("equipment_items", MANAGER_COLUMNS, (q) => q.is("deleted_at", null))
        : await fetchAllPaginated<DBEquipmentItem>("equipment_items_public", PUBLIC_COLUMNS);
      return rows.sort((a, b) => a.name.localeCompare(b.name, "nb"));
    },
  });
}

export function useEquipmentItem(id: string | undefined, canManage: boolean) {
  return useQuery({
    queryKey: ["equipment-detail", id, { canManage }],
    enabled: !!id,
    queryFn: async (): Promise<DBEquipmentItem | null> => {
      const query = canManage
        ? supabase.from("equipment_items").select(MANAGER_COLUMNS).is("deleted_at", null)
        : supabase.from("equipment_items_public").select(PUBLIC_COLUMNS);
      const { data, error } = await query.eq("id", id!).maybeSingle();
      if (error) throw error;
      return data as DBEquipmentItem | null;
    },
  });
}

export interface EquipmentHistoryEntry {
  id: string;
  event_type: string;
  payload: Record<string, unknown> | null;
  created_at: string;
  users: { full_name: string } | null;
}

/** Historikk fra event_log for ett utstyr (audit-triggere og importen). */
export function useEquipmentHistory(id: string | undefined) {
  return useQuery({
    queryKey: ["equipment-history", id],
    enabled: !!id,
    queryFn: async (): Promise<EquipmentHistoryEntry[]> => {
      const { data, error } = await supabase
        .from("event_log")
        .select("id, event_type, payload, created_at, users:user_id(full_name)")
        .eq("entity_type", "equipment_items")
        .eq("entity_id", id!)
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as unknown as EquipmentHistoryEntry[];
    },
  });
}
